import './index.scss';

import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { BrandLogo } from '@/components/BrandLogo';
import { Icon } from '@/components/Icon';
import { toggleSidebar } from '@/core/redux/themeSettingsSlice';
import type { AppDispatch } from '@/store/store';
import { getActivePath, isItemActive, type NavItem } from '../navigation';
import { ProfileMenu } from '../ProfileMenu';
import { SidebarMenuItem } from '../SidebarMenuItem';

type SidebarProps = {
  items: NavItem[];
  accountItem?: NavItem;
  onOpenUpdates: () => void;
  onOpenSupport: () => void;
  /** Icon rail: labels move into tooltips, the module search is hidden. */
  collapsed?: boolean;
  /** The expand/collapse button; hidden while the viewport forces the rail and in the drawer. */
  showToggle?: boolean;
  /** Called after a destination is picked, e.g. to close the drawer. */
  onNavigate?: () => void;
};

type NavSection = {
  key: string;
  label: string;
  items: NavItem[];
};

export const Sidebar = ({
  items,
  accountItem,
  onOpenUpdates,
  onOpenSupport,
  collapsed = false,
  showToggle = false,
  onNavigate,
}: SidebarProps) => {
  const { t, i18n } = useTranslation(['common', 'sidebar-menu']);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const groupIdPrefix = useId();
  const [query, setQuery] = useState('');
  const activePath = getActivePath(pathname, accountItem ? [accountItem, ...items] : items);
  const normalizedQuery = collapsed ? '' : query.trim().toLocaleLowerCase(i18n.language);
  const matchesQuery = (label: string) =>
    label.toLocaleLowerCase(i18n.language).includes(normalizedQuery);
  // Single-level modules share the "Modules" group; every module with sub-modules gets its own group.
  const sections: NavSection[] = [
    {
      key: 'modules',
      label: t('sidebar-menu:sidebar-menu.modules'),
      items: items.filter((item) => !item.children?.length),
    },
    ...items
      .filter((item) => item.children?.length)
      .map((group) => ({ key: group.key, label: group.label, items: group.children ?? [] })),
  ]
    .map((section) =>
      !normalizedQuery || matchesQuery(section.label)
        ? section
        : { ...section, items: section.items.filter((item) => matchesQuery(item.label)) }
    )
    .filter((section) => section.items.length > 0);
  const toggleLabel = collapsed ? t('common:common.expand_menu') : t('common:common.collapse_menu');

  const handleSelect = (item: NavItem) => {
    onNavigate?.();

    if (item.onClick) {
      item.onClick();
      return;
    }

    if (item.path) {
      navigate(item.path);
    }
  };

  return (
    <aside className={`c-aside${collapsed ? ' c-aside--collapsed' : ''}`}>
      <div className="c-aside__brand-row">
        <BrandLogo />
      </div>
      {!collapsed && (
        <label className="c-aside__search">
          <Icon name="search" size={16} />
          <input
            type="search"
            value={query}
            placeholder={t('sidebar-menu:sidebar-menu.searchModules')}
            aria-label={t('sidebar-menu:sidebar-menu.searchModules')}
            autoComplete="off"
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
      )}
      <nav className="c-aside__nav" aria-label={t('common:common.main_navigation')}>
        {sections.map((section) => (
          <div
            key={section.key}
            className="c-aside__group"
            role="group"
            aria-labelledby={`${groupIdPrefix}-${section.key}`}
          >
            <span id={`${groupIdPrefix}-${section.key}`} className="c-aside__group-label">
              {section.label}
            </span>
            {section.items.map((item) => (
              <SidebarMenuItem
                key={item.key}
                item={item}
                active={isItemActive(item, activePath)}
                collapsed={collapsed}
                onSelect={handleSelect}
              />
            ))}
          </div>
        ))}
        {sections.length === 0 && (
          <p className="c-aside__empty" role="status">
            {t('sidebar-menu:sidebar-menu.noModule')}
          </p>
        )}
      </nav>
      <div className={`c-sidebar-account${collapsed ? ' c-sidebar-account--collapsed' : ''}`}>
        <ProfileMenu
          accountItem={accountItem}
          collapsed={collapsed}
          onNavigate={onNavigate}
          onOpenUpdates={onOpenUpdates}
          onOpenSupport={onOpenSupport}
        />
      </div>
      {showToggle && (
        <button
          type="button"
          aria-expanded={!collapsed}
          className="c-aside__toggle"
          data-tooltip={collapsed ? toggleLabel : undefined}
          onClick={() => dispatch(toggleSidebar())}
        >
          <Icon name={collapsed ? 'panelLeftOpen' : 'panelLeftClose'} />
          <span className={collapsed ? 'c-aside__sr-only' : 'c-aside__toggle-label'}>
            {toggleLabel}
          </span>
        </button>
      )}
    </aside>
  );
};
